import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiLoading, mdiCodeJson, mdiStopCircle } from '@mdi/js';
import { useSyncProgress, cancelarSync, phaseLabel, SyncProgressDTO } from '@/lib/syncJob';
type SyncAcaoStatusDTO = 'success' | 'error';
type SyncSessaoStatusDTO = 'running' | 'success' | 'error' | 'partial' | 'cancelled';
type SyncAcaoTipoDTO = 'add' | 'update' | 'remove' | 'move' | 'command';

type SyncAcaoDTO = {
    id: number;
    sessao_id: number;
    area: string;
    tipo: SyncAcaoTipoDTO;
    dados?: Record<string, unknown> | null;
    anterior?: Record<string, unknown> | null;
    status: SyncAcaoStatusDTO;
    erro?: string | null;
    data_criacao: string;
};

type SyncSessaoDTO = {
    id: number;
    appliance_id: number;
    data_inicio: string;
    data_fim?: string | null;
    status: SyncSessaoStatusDTO;
    erro?: string | null;
    acoes?: SyncAcaoDTO[];
};

type Props = {
    applianceId: number;
    applianceNome?: string;
    show: boolean;
    onClose: () => void;
};

const STATUS_LABEL: Record<string, string> = {
    running: 'Em andamento',
    success: 'Sucesso',
    error: 'Erro',
    partial: 'Parcial',
    cancelled: 'Cancelado',
};

const STATUS_COLOR: Record<string, string> = {
    running: 'var(--tc-color-blue-500)',
    success: 'var(--tc-color-green-600)',
    error: 'var(--tc-color-red-600)',
    partial: 'var(--tc-color-yellow-600)',
    cancelled: 'var(--tc-color-orange-600)',
};

const TIPO_LABEL: Record<string, string> = {
    add: 'Adicionar',
    update: 'Atualizar',
    remove: 'Remover',
    move: 'Mover',
    command: 'Evento',
};

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
}

function duracao(inicio: string, fim?: string | null): string {
    if (!fim) return '-';
    const ms = new Date(fim).getTime() - new Date(inicio).getTime();
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

const CAMPOS_OCULTOS = new Set(['.id', 'place-before']);

const DadosCell = ({ dados, anterior }: { dados: Record<string, unknown> | null | undefined; anterior?: Record<string, unknown> | null }) => {
    const [expanded, setExpanded] = useState(false);

    if (!dados || Object.keys(dados).length === 0) {
        return <span style={{ color: 'var(--tc-color-gray-400)' }}>-</span>;
    }

    const entries = Object.entries(dados).filter(([k]) => !CAMPOS_OCULTOS.has(k));

    if (entries.length === 0) {
        return <span style={{ color: 'var(--tc-color-gray-400)' }}>-</span>;
    }

    const PREVIEW_COUNT = 3;
    const preview = entries.slice(0, PREVIEW_COUNT);
    const rest = entries.slice(PREVIEW_COUNT);

    const renderEntry = ([k, v]: [string, unknown]) => {
        const anteriorVal = anterior?.[k];
        const changed = anterior != null && k in anterior && String(anteriorVal ?? '') !== String(v ?? '');
        return (
            <React.Fragment key={k}>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--tc-color-gray-500)', flexShrink: 0 }}>{k}:</span>
                    <span
                        style={{
                            color: 'var(--tc-color-gray-800)',
                            wordBreak: 'break-all',
                            background: changed ? 'var(--tc-color-blue-50)' : undefined,
                            borderRadius: changed ? '2px' : undefined,
                            padding: changed ? '0 2px' : undefined,
                        }}
                    >
                        {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
                    </span>
                </div>
                {changed && (
                    <div
                        style={{
                            display: 'flex',
                            gap: '4px',
                            flexWrap: 'wrap',
                            color: 'var(--tc-color-gray-400)',
                            marginBottom: '2px',
                            paddingLeft: '8px',
                        }}
                    >
                        <span style={{ flexShrink: 0 }}>↑ era:</span>
                        <span style={{ wordBreak: 'break-all', textDecoration: 'line-through', opacity: 0.75 }}>
                            {typeof anteriorVal === 'object'
                                ? JSON.stringify(anteriorVal)
                                : String(anteriorVal ?? '')}
                        </span>
                    </div>
                )}
            </React.Fragment>
        );
    };

    return (
        <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.5' }}>
            {(expanded ? entries : preview).map(renderEntry)}
            {rest.length > 0 && (
                <button
                    onClick={() => setExpanded((s) => !s)}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: '0',
                        cursor: 'pointer',
                        color: 'var(--tc-color-blue-500)',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        marginTop: '2px',
                    }}
                >
                    <Icon path={mdiCodeJson} size={0.55} />
                    {expanded ? 'menos' : `+${rest.length} campo(s)`}
                </button>
            )}
        </div>
    );
};

const AcoesTable = ({ acoes }: { acoes: SyncAcaoDTO[] }) => {
    if (!acoes.length) {
        return (
            <p style={{ color: 'var(--tc-color-gray-500)', fontSize: '13px', margin: '8px 0 0 0' }}>
                Nenhuma ação executada nesta sessão.
            </p>
        );
    }
    return (
        <div style={{ overflowX: 'auto', marginTop: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr style={{ background: 'var(--tc-color-gray-100)', textAlign: 'left' }}>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Área</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Tipo</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Parâmetros</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Status</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Erro / Detalhe</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>Data/hora</th>
                    </tr>
                </thead>
                <tbody>
                    {acoes.map((a) => (
                        <tr key={a.id} style={{ borderBottom: '1px solid var(--tc-color-gray-200)' }}>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: 'var(--tc-color-gray-700)' }}>
                                {a.area}
                            </td>
                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{TIPO_LABEL[a.tipo] ?? a.tipo}</td>
                            <td style={{ padding: '6px 10px', maxWidth: '320px' }}>
                                <DadosCell dados={a.dados} anterior={a.anterior} />
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                                <span
                                    style={{
                                        color: a.status === 'success' ? 'var(--tc-color-green-600)' : 'var(--tc-color-red-600)',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {a.status === 'success' ? '✓ Sucesso' : '✗ Erro'}
                                </span>
                            </td>
                            <td style={{ padding: '6px 10px', color: 'var(--tc-color-gray-600)', maxWidth: '220px', wordBreak: 'break-all' }}>
                                {a.erro ?? '-'}
                            </td>
                            <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--tc-color-gray-500)' }}>
                                {formatDate(a.data_criacao)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const SessaoRow = ({
    sessao,
    applianceId,
}: {
    sessao: SyncSessaoDTO;
    applianceId: number;
}) => {
    const [expanded, setExpanded] = useState(false);
    const [cancelando, setCancelando] = useState(false);
    const queryClient = useQueryClient();

    const queryDetail = useQuery(
        ['sync-log-detail', sessao.id],
        async () => {
            const res = await axios.get<{ ok: boolean; result: SyncSessaoDTO }>(
                `/api/appliance/${applianceId}/sync-historico/${sessao.id}`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        // Sessão em andamento ganha novas ações durante o sync — sem staleTime.
        { enabled: expanded, staleTime: sessao.status === 'running' ? 0 : 30_000 }
    );

    // Progresso ao vivo enquanto running (polling 2s; some quando a sessão termina).
    const progressQuery = useSyncProgress(
        sessao.status === 'running' ? applianceId : null,
        sessao.status === 'running' ? sessao.id : null,
    );
    const progresso = progressQuery.data?.progress ?? null;

    // Transição running → finalizada: recarrega a lista (e o detalhe) para
    // refletir o status final e as ações persistidas.
    React.useEffect(() => {
        if (sessao.status === 'running' && progressQuery.data && !progressQuery.data.running) {
            queryClient.invalidateQueries(['sync-log', applianceId]);
            queryClient.invalidateQueries(['sync-log-detail', sessao.id]);
        }
    }, [sessao.status, progressQuery.data, queryClient, applianceId, sessao.id]);

    const handleCancelar = async () => {
        if (!window.confirm('Cancelar este sincronismo? As ações já aplicadas permanecem no equipamento.')) {
            return;
        }
        setCancelando(true);
        await cancelarSync(applianceId, sessao.id);
        setCancelando(false);
    };

    const acoes = queryDetail.data?.acoes ?? sessao.acoes ?? [];

    return (
        <div
            style={{
                border: '1px solid var(--tc-color-gray-200)',
                borderRadius: '6px',
                overflow: 'hidden',
            }}
        >
            <button
                onClick={() => setExpanded((v) => !v)}
                style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '10px 12px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                <Icon
                    path={expanded ? mdiChevronDown : mdiChevronRight}
                    size={0.8}
                    color="var(--tc-color-gray-500)"
                />
                <span
                    style={{
                        fontWeight: 600,
                        color: STATUS_COLOR[sessao.status] ?? 'inherit',
                        minWidth: '90px',
                    }}
                >
                    {STATUS_LABEL[sessao.status] ?? sessao.status}
                </span>
                <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-700)', flex: 1 }}>
                    {formatDate(sessao.data_inicio)}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)', marginRight: '8px' }}>
                    duração: {sessao.status === 'running' ? 'em andamento' : duracao(sessao.data_inicio, sessao.data_fim)}
                </span>
                {sessao.status === 'running' ? (
                    <span
                        style={{
                            fontSize: '12px',
                            color: 'var(--tc-color-blue-600)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginRight: '8px',
                        }}
                    >
                        <Icon path={mdiLoading} size={0.6} spin />
                        {progresso
                            ? `${phaseLabel(progresso.phase)}${progresso.area_total ? ` (${(progresso.area_index ?? 0) + 1}/${progresso.area_total})` : ''}`
                            : 'aguardando progresso do agente...'}
                    </span>
                ) : (
                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                        {(sessao.acoes?.length ?? 0)} ação(ões)
                    </span>
                )}
                {sessao.status === 'running' && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            void handleCancelar();
                        }}
                        style={{
                            fontSize: '12px',
                            color: cancelando ? 'var(--tc-color-gray-400)' : 'var(--tc-color-red-600)',
                            cursor: cancelando ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <Icon path={mdiStopCircle} size={0.6} />
                        {cancelando ? 'cancelando...' : 'Cancelar'}
                    </span>
                )}
            </button>

            {expanded && (
                <div style={{ padding: '0 12px 12px 12px', background: 'var(--tc-color-gray-50)' }}>
                    {sessao.erro && (
                        <p
                            style={{
                                margin: '8px 0',
                                padding: '8px',
                                background: 'var(--tc-color-red-50)',
                                borderRadius: '4px',
                                color: 'var(--tc-color-red-700)',
                                fontSize: '13px',
                            }}
                        >
                            <strong>Erro:</strong> {sessao.erro}
                        </p>
                    )}
                    {queryDetail.isLoading ? (
                        <HBox style={{ padding: '12px', justifyContent: 'center' }}>
                            <Icon path={mdiLoading} size={1} spin />
                        </HBox>
                    ) : (
                        <AcoesTable acoes={acoes} />
                    )}
                </div>
            )}
        </div>
    );
};

const SyncLogModal = ({ applianceId, applianceNome, show, onClose }: Props) => {
    const queryList = useQuery(
        ['sync-log', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; result: SyncSessaoDTO[] }>(
                `/api/appliance/${applianceId}/sync-historico`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        { enabled: show, staleTime: 10_000 }
    );

    return (
        <Modal
            show={show}
            title={`Histórico de sincronização${applianceNome ? ` — ${applianceNome}` : ''}`}
            onClose={onClose}
            hideControls
            size="lg"
        >
            {queryList.isLoading && (
                <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                    <Icon path={mdiLoading} size={1.5} spin />
                </HBox>
            )}

            {queryList.isError && (
                <p style={{ color: 'var(--tc-color-red-600)', padding: '16px 0' }}>
                    Erro ao carregar o histórico. Tente novamente.
                </p>
            )}

            {!queryList.isLoading && !queryList.isError && (
                <VBox style={{ gap: '8px' }}>
                    {!queryList.data?.length && (
                        <p style={{ color: 'var(--tc-color-gray-500)', padding: '16px 0' }}>
                            Nenhuma sincronização registrada para este appliance.
                        </p>
                    )}
                    {queryList.data?.map((sessao) => (
                        <SessaoRow
                            key={sessao.id}
                            sessao={sessao}
                            applianceId={applianceId}
                        />
                    ))}
                </VBox>
            )}
        </Modal>
    );
};

export default SyncLogModal;
